"use client";

import { Button } from "@/components/ui/button";
import { DeleteIcon } from "@/components/ui/delete";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { FileManagerTreeNode } from "@/hooks/use-file-manager-tree";
import { MoreHorizontalIcon } from "lucide-react";
import { useState } from "react";
import { KbNodeDetailsDialog } from "./kb-node-details-dialog";

export type KbMoveTargets = {
  /** Other teams the file could be requested into. */
  teams: { id: string; name: string }[];
  /** Collections in the file's own team, plus the team root when applicable. */
  collections: { id: string; name: string }[];
};

export type KbRowActionsProps = {
  node: FileManagerTreeNode;
  canEditMetadata: boolean;
  canMoveTeam: boolean;
  canDelete: boolean;
  moveTargets: KbMoveTargets;
  onDelete: (node: FileManagerTreeNode) => void;
  onPreview: (node: FileManagerTreeNode) => void;
  onRetry: (node: FileManagerTreeNode) => void;
  onViewChunks: (node: FileManagerTreeNode) => void;
  onDownload: (node: FileManagerTreeNode) => void;
  onEditMetadata: (node: FileManagerTreeNode) => void;
  onMoveToTeam: (node: FileManagerTreeNode, teamId: string) => void;
  onMoveToCollection: (node: FileManagerTreeNode, targetNodeId: string) => void;
};

/**
 * Row overflow menu. "Move to…" is the keyboard-accessible equivalent of
 * dragging the row onto a team card or collection chip.
 */
export function KbRowActions({
  node,
  canEditMetadata,
  canMoveTeam,
  canDelete,
  moveTargets,
  onDelete,
  onPreview,
  onRetry,
  onViewChunks,
  onDownload,
  onEditMetadata,
  onMoveToTeam,
  onMoveToCollection,
}: KbRowActionsProps) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const hasTeamTargets = canMoveTeam && moveTargets.teams.length > 0;
  const hasCollectionTargets = moveTargets.collections.length > 0;

  return (
    <>
      <KbNodeDetailsDialog node={node} open={detailsOpen} onOpenChange={setDetailsOpen} />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label={`More actions for ${node.name}`}
            className="size-8 shrink-0 text-muted-foreground hover:text-foreground"
            onClick={(event) => event.stopPropagation()}
          >
            <MoreHorizontalIcon className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(event) => event.stopPropagation()}>
          <DropdownMenuItem onSelect={() => onPreview(node)}>Preview</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setDetailsOpen(true)}>Details</DropdownMenuItem>
          {canEditMetadata && (
            <DropdownMenuItem onSelect={() => onEditMetadata(node)}>
              Edit metadata
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onSelect={() => onViewChunks(node)}>Chunks</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => onDownload(node)}>Download</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => onRetry(node)}>Retry</DropdownMenuItem>

          {hasTeamTargets || hasCollectionTargets ? (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>Move to…</DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="max-h-72 overflow-y-auto">
                {hasCollectionTargets ? (
                  <>
                    <DropdownMenuLabel className="text-xs text-muted-foreground">
                      Collections
                    </DropdownMenuLabel>
                    {moveTargets.collections.map((collection) => (
                      <DropdownMenuItem
                        key={collection.id}
                        onSelect={() => onMoveToCollection(node, collection.id)}
                      >
                        {collection.name}
                      </DropdownMenuItem>
                    ))}
                  </>
                ) : null}
                {hasTeamTargets && hasCollectionTargets ? <DropdownMenuSeparator /> : null}
                {hasTeamTargets ? (
                  <>
                    <DropdownMenuLabel className="text-xs text-muted-foreground">
                      Teams
                    </DropdownMenuLabel>
                    {moveTargets.teams.map((team) => (
                      <DropdownMenuItem key={team.id} onSelect={() => onMoveToTeam(node, team.id)}>
                        {team.name}
                      </DropdownMenuItem>
                    ))}
                  </>
                ) : null}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          ) : null}

          {canDelete ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onSelect={() => onDelete(node)}
              >
                <DeleteIcon className="size-4" />
                Delete
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
